import { Skeleton } from "@Light/components/page/Skeleton";
import type { PlanValueProp } from "@Light/services/lightTypes";
import { LightIcon } from "@Light/services/lightTypes";
import {
  BatteryCharging01,
  BatteryFull,
  Car02,
  CheckCircle,
  Clock,
  CloudLightning,
  Coins03,
  HeartHand,
  LayersTwo02,
  PiggyBank02,
  Sun,
  Tag02,
  Tool02,
  Truck01,
} from "@untitledui/icons";
import { clsx } from "clsx";
import { type FC, type ReactNode, type SVGProps } from "react";
import { Tooltip } from "react-tooltip";
import { twMerge } from "tailwind-merge";

type PlanValuePropsProps = {
  valueProps: PlanValueProp[];
  isLoading?: boolean;
};

const Leaf01Icon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg
    xmlns="http://w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    {...props}
  >
    <path
      d="M3.84684 15.1212C3.84684 15.1212 0.719947 9.78189 6.48151 5.46083C13.5072 0.191712 20.972 5.02186 20.972 5.02186M3.40772 20.3907C3.40772 20.3907 2.52949 16.4387 5.16413 13.8041C7.79877 11.1694 12.1899 9.41315 12.1899 9.41315M6.04242 17.3172C6.04242 17.3172 10.5 18.9999 13 16.9999C15.5 14.9999 17.0001 13.9997 18.0001 9.49981C19.0001 4.9999 20.9719 5.08041 20.9719 5.08041"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SKELETON_WIDTHS = ["w-full", "w-4/5", "w-11/12"];

const ICON_MAP: Record<LightIcon, FC<SVGProps<SVGSVGElement>>> = {
  [LightIcon.BatteryCharging]: BatteryCharging01,
  [LightIcon.BatteryFull]: BatteryFull,
  [LightIcon.Car]: Car02,
  [LightIcon.CheckCircle]: CheckCircle,
  [LightIcon.Clock]: Clock,
  [LightIcon.CloudLightning]: CloudLightning,
  [LightIcon.Coins]: Coins03,
  [LightIcon.HeartHand]: HeartHand,
  [LightIcon.Layers]: LayersTwo02,
  [LightIcon.Leaf]: Leaf01Icon,
  [LightIcon.PiggyBank]: PiggyBank02,
  [LightIcon.Sun]: Sun,
  [LightIcon.Tag]: Tag02,
  [LightIcon.Tool]: Tool02,
  [LightIcon.Truck]: Truck01,
};

export const PlanValueProps = ({
  valueProps,
  isLoading,
}: PlanValuePropsProps) => {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-3xl max-w-90 w-full">
        {SKELETON_WIDTHS.map((width, index) => (
          <div key={index} className="flex items-center gap-2xl">
            <Skeleton className="size-10 rounded-full shrink-0" />
            <div className="flex-1">
              <Skeleton className={clsx("h-4", width)} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (!valueProps.length) return null;
  return (
    <div className="flex flex-col gap-3xl max-w-90">
      {valueProps.map((vp) => {
        const Icon = ICON_MAP[vp.icon] ?? CheckCircle;
        return (
          <div key={vp.id} className="flex items-center gap-2xl">
            <div className="flex items-center justify-center p-2.5 bg-success-25 rounded-full shrink-0">
              <Icon className="size-5 text-green-700 stroke-[1.66px]" />
            </div>
            <span className="text-sm font-medium text-primary">
              {renderTextWithCallouts(vp)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const CalloutTooltip = ({
  id,
  text,
  className,
  tooltipClassName,
  children,
}: {
  id: string;
  text: string;
  className?: string;
  tooltipClassName?: string;
  children: ReactNode;
}) => (
  <span>
    <span
      data-tooltip-id={id}
      className={twMerge(
        "cursor-help underline decoration-dotted underline-offset-2",
        className,
      )}
    >
      {children}
    </span>
    <Tooltip id={id} place="top" delayShow={300} className={tooltipClassName}>
      <div className="max-w-56 text-center text-xs">{text}</div>
    </Tooltip>
  </span>
);

/**
 * Renders a value proposition's text, wrapping any substring named by a
 * callout's matches in an underlined tooltip that shows the callout text.
 */
const renderTextWithCallouts = ({ id, text, callouts }: PlanValueProp) => {
  const matches = callouts
    .map((callout, index) => ({
      callout,
      index,
      start: text.indexOf(callout.matches),
    }))
    .filter(({ start }) => start !== -1)
    .sort((a, b) => a.start - b.start);

  if (!matches.length) return text;

  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  matches.forEach(({ callout, index, start }) => {
    const end = start + callout.matches.length;
    const tooltipId = `value-prop-${id}-callout-${index}`;
    if (start > cursor) nodes.push(text.slice(cursor, start));
    nodes.push(
      <CalloutTooltip key={tooltipId} id={tooltipId} text={callout.text}>
        {text.slice(start, end)}
      </CalloutTooltip>,
    );
    cursor = end;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));

  return nodes;
};
